import React, { useState, useEffect } from "react";
import {
  Search,
  Eye,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users,
  Plus,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import DeleteAlertModal from "../../components/common/DeleteAlertModal";
import AdminSellerEditModal from "../../components/AdminSideComponents/AdminSeller/AdminSellerEditModal";
import AdminSellerViewModal from "../../components/AdminSideComponents/AdminSeller/AdminSellerViewModal";
import { useNavigate } from "react-router-dom";

const AdminSellers = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  // const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [selectedSeller, setSelectedSeller] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dltLoading, setDltLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  // Fetch Sellers API
  const fetchSellers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/supplier/suppliers`);
      setSellers(res.data.data || []);
    } catch (err) {
      toast.error("Failed to load sellers!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSellers();
  }, []);

  // Filtering Sellers
  const filteredSellers = sellers.filter((s) => {
    const q = searchTerm.toLowerCase();
    return (
      s.fullName?.toLowerCase().includes(q) ||
      s.businessName?.toLowerCase().includes(q) ||
      s.mobNo?.toLowerCase().includes(q) ||
      s.emailId?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filteredSellers.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentSellers = filteredSellers.slice(startIndex, endIndex);

  // Delete Seller
  const handleDeleteSeller = async () => {
    try {
      setDltLoading(true);
      await axios.delete(`${API_URL}/supplier/delete-suppliers/${deleteTarget._id}`);
      toast.success("Seller deleted!");
      setIsDeleteOpen(false);
      fetchSellers();
    } catch (err) {
      toast.error("Failed to delete seller!");
    } finally {
      setDltLoading(false);
    }
  };

  // Update Status
  const handleStatusChange = async (seller, newStatus) => {
    try {
      await axios.put(`${API_URL}/supplier/change-suppliers-status/${seller._id}`, {
        status: newStatus,
      });
      toast.success("Status updated!");
      fetchSellers();
    } catch (err) {
      toast.error("Failed to update status!");
    }
  };

  const SkeletonRow = () => (
    <tr className="border-b border-gray-100">
      {[...Array(7)].map((_, i) => (
        <td key={i} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );

  return (
    <div className="min-h-screen">
      <div className="w-full mx-auto px-4 md:px-10">

        {/* Header */}
        <div className="flex justify-between items-center my-6">
          <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
            <Users className="w-7 h-7 text-primaryColor" />
            Seller Management
          </h1>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">

            <div className="relative">
              <input
                type="text"
                placeholder="Search sellers..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:outline-none focus:ring-primaryColor"
              />
              <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
            
            <button
              onClick={() => {
                setSelectedSeller(null);
                setIsEditOpen(true);
              }}
              className="bg-primaryColor text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primaryDark transition-colors"
            >
              <Plus size={20} />
              Add Seller
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-sm font-semibold">#</th>
                  <th className="px-4 py-4 text-sm font-semibold">Image</th>
                  <th className="px-4 py-4 text-sm font-semibold">Name</th>
                  <th className="px-4 py-4 text-sm font-semibold">Business</th>
                  <th className="px-4 py-4 text-sm font-semibold">Phone</th>
                  <th className="px-4 py-4 text-sm font-semibold">Email</th>
                  <th className="px-4 py-4 text-sm font-semibold">Status</th>
                  <th className="px-4 py-4 text-sm font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
                ) : currentSellers.length > 0 ? (
                  currentSellers.map((s, index) => (
                    <tr key={s._id} className="border-b hover:bg-gray-50">

                      <td className="px-4 py-4">
                        {(currentPage - 1) * entriesPerPage + index + 1}
                      </td>

                      <td className="px-4 py-4">
                        <img
                          src={s.shopImage}
                          alt={s.fullName}
                          className="w-14 h-14 object-cover rounded-lg border"
                        />
                      </td>

                      <td className="px-4 py-4 font-medium">{s.fullName}</td>

                      <td className="px-4 py-4">{s.businessName}</td>

                      <td className="px-4 py-4">{s.mobNo}</td>

                      <td className="px-4 py-4">{s.emailId}</td>

                      {/* Status Dropdown */}
                      <td className="px-4 py-4">
                        <select
                          value={s.status}
                          onChange={(e) => handleStatusChange(s, e.target.value)}
                          className="border px-2 py-1 rounded"
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="flex gap-2">

                          <button
                            className="p-2 hover:bg-blue-50 rounded"
                            title="View"
                            onClick={() => navigate(`/admin/sellers/${s._id}`)}
                          >
                            <Eye className="w-4 h-4 text-[#ea8d2a]" />
                          </button>

                          <button
                            className="p-2 hover:bg-green-50 rounded"
                            title="Edit"
                            onClick={() => {
                              setSelectedSeller(s);
                              setIsEditOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4 text-[#6278e8]" />
                          </button>

                          <button
                            className="p-2 hover:bg-red-50 rounded"
                            title="Delete"
                            onClick={() => {
                              setDeleteTarget(s);
                              setIsDeleteOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>


                        </div>
                      </td>

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      No sellers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && filteredSellers.length > 0 && (
            <div className="px-6 py-4 border-t flex justify-between">

              <div>
                Showing {startIndex + 1}–{Math.min(endIndex, filteredSellers.length)} of{" "}
                {filteredSellers.length}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border rounded disabled:opacity-50"
                >
                  <ChevronLeft />
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 rounded ${currentPage === i + 1
                      ? "bg-blue-500 text-white"
                      : "border"
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border rounded disabled:opacity-50"
                >
                  <ChevronRight />
                </button>
              </div>

            </div>
          )}

        </div>

      </div>
      {/* <AdminSellerViewModal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        seller={selectedSeller}
      /> */}

      <AdminSellerEditModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        seller={selectedSeller}
        onSuccess={fetchSellers}
      />

      <DeleteAlertModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        dltType="seller"
        dltName={deleteTarget?.fullName}
        onConfirm={handleDeleteSeller}
        dltLoading={dltLoading}
      />
    </div>
  );
};

export default AdminSellers;
